/**
 * Northeast and Mid-Atlantic craft malthouses — Blue Ox, CNC Malt, Deer Creek,
 * Double Eagle, Hudson Valley Malt, Maine Malt House, Murphy & Rude, New York
 * Craft Malt, Rabbit Hill, Valley Malt, Bear Branch and Thrall Family Malt.
 *
 * Floor-malting is the house style for most of these operations. Numbers are
 * class-typical unless a colour or extract figure is taken from a published
 * sheet; see {@link craftMalt}.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** New England, New York and Mid-Atlantic craft malts. @see Fermentable */
export const northeastCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Blue Ox Malthouse — Lisbon Falls, Maine
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'blue-ox-yankee-pils',
    name: 'Yankee Pils',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.3,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted New England barley kilned as pale as a pilsner gets. Clean bread and hay with a straw wort, built for lagers, saisons and hop-forward pale beers that need a quiet malt floor.',
    tags: ['base', 'pilsner', 'craft-malt', 'maine', 'floor-malted', 'local'],
    url: 'https://www.blueoxmalthouse.com/merch-house/5lb-yankee-pilsner-malt',
  }),
  craftMalt({
    id: 'blue-ox-vyenna',
    name: 'Vyenna',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house Vienna, floor-malted and kilned just dark enough for toasted-cracker depth. Still self-converting, so it can carry a festbier or season a pale ale at 10–30 %.',
    tags: ['kilned', 'vienna', 'craft-malt', 'maine', 'floor-malted'],
    url: 'https://www.blueoxmalthouse.com/merch-house/vyenna-malt',
  }),
  craftMalt({
    id: 'blue-ox-light-munich',
    name: 'Light Munich',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Award-winning floor-malted Munich with bread-crust sweetness and enough enzyme to convert itself. The usual seasoning for bocks, dunkels and malt-forward American ambers.',
    tags: ['kilned', 'munich', 'craft-malt', 'maine', 'floor-malted'],
  }),
  craftMalt({
    id: 'blue-ox-wheat',
    name: 'Wheat Malt',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Floor-malted regional wheat, husk-free and high in protein. Builds foam and a silky body in wheat beers and hazy IPAs; rice hulls help the lauter once it passes about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'maine', 'floor-malted', 'head-retention'],
    url: 'https://www.blueoxmalthouse.com/merch-house/wheat-malt',
  }),
  craftMalt({
    id: 'blue-ox-caramel-60',
    name: 'Caramel 60',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Mid-color crystal from floor-malted Maine barley, giving clean caramel sweetness and a copper cast. Standard in amber and brown ales at 5–10 %; push much past that and the beer turns cloying.',
    tags: ['crystal', 'caramel', 'craft-malt', 'maine', 'floor-malted'],
    url: 'https://www.blueoxmalthouse.com/merch-house/caramel-60',
  }),
  craftMalt({
    id: 'blue-ox-maplewood-smoked',
    name: 'Maplewood Smoked',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted base malt smoked over maple. Sweeter and less phenolic than beechwood rauchmalz, so it can be the whole grist of a smoked lager or a 5–15 % accent in porter and brown ale.',
    tags: ['smoked', 'maple', 'craft-malt', 'maine', 'floor-malted', 'base'],
    url: 'https://www.blueoxmalthouse.com/merch-house/maplewood-smoked-malt',
  }),
  craftMalt({
    id: 'blue-ox-chocolate-rye',
    name: 'Chocolate Rye',
    maker: 'Blue Ox Malthouse',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Rye roasted to chocolate color, pairing cocoa and coffee with rye pepper. A small addition in dark rye beers, roggenbock and anything that needs roast without a husky bite.',
    tags: ['roasted', 'rye', 'chocolate', 'craft-malt', 'maine'],
    url: 'https://www.blueoxmalthouse.com/merch-house/chocolate-rye',
  }),
  craftMalt({
    id: 'blue-ox-oat',
    name: 'Oat Malt',
    maker: 'Blue Ox Malthouse',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Floor-malted oats with a granola note and enough enzyme to convert themselves in a well-balanced mash. Gives hazy IPAs and stouts creamy weight with less gumminess than flaked oats.',
    tags: ['adjunct', 'oats', 'craft-malt', 'maine', 'floor-malted', 'mouthfeel'],
  }),
  craftMalt({
    id: 'blue-ox-pale',
    name: 'Pale Malt',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house pale: Maine two-row, kilned for a bready, faintly nutty base that will carry any ale on its own.',
    tags: ['base', 'ale', 'pale', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-dark-munich',
    name: 'Dark Munich',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Deep Munich: bread crust, toffee and dark honey, with just enough enzyme to be a large part of a grist. Where a dunkel or a bock gets its color and most of its malt character.',
    tags: ['kilned', 'munich', 'dark', 'malty', 'dunkel', 'craft-malt', 'maine'],
    url: 'https://www.blueoxmalthouse.com/merch-house/dark-munich',
  }),
  craftMalt({
    id: 'blue-ox-extra-dark-munich',
    name: 'Extra Dark Munich',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 73,
    moisturePercent: 4.5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Past Dark Munich and into caramel territory, with essentially no enzyme left. A color and depth malt for doppelbock and dark lager.',
    tags: ['kilned', 'munich', 'dark', 'caramel', 'bock', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-light-caramel',
    name: 'Light Caramel',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 15,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The palest of the caramel range: light honey and a touch of biscuit, adding body and head without much color.',
    tags: ['crystal', 'caramel', 'light', 'body', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-caramel-30',
    name: 'Caramel 30',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Gold to light amber with clean caramel sweetness. The everyday caramel malt for pale ales and ambers.',
    tags: ['crystal', 'caramel', 'amber', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-crystal-30',
    name: 'Crystal 30',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Stewed and kilned to 30: caramel and light toffee, a shade drier than the caramel malt of the same color.',
    tags: ['crystal', 'caramel', 'amber', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-crystal-60',
    name: 'Crystal 60',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Dark toffee and the first of the dried fruit. Where an English bitter or a brown ale gets its sweetness.',
    tags: ['crystal', 'caramel', 'toffee', 'english', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-crystal-90',
    name: 'Crystal 90',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Raisin and dark treacle over a firm caramel base. Ten percent is plenty in most beers.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-crystal-120',
    name: 'Crystal 120 (Special Moxie)',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The dark end of the range, and named for the Maine soft drink: prune, burnt sugar and a faintly medicinal edge that is the whole joke. Small additions only.',
    tags: ['crystal', 'caramel', 'dark', 'prune', 'moxie', 'craft-malt', 'maine'],
    url: 'https://www.blueoxmalthouse.com/merch-house/special-moxie',
  }),
  craftMalt({
    id: 'blue-ox-crystal-rye',
    name: 'Crystal Rye',
    maker: 'Blue Ox Malthouse',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 75,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Rye stewed rather than barley: pumpernickel and rye spice over dark caramel. It changes the character of a beer more than the color.',
    tags: ['crystal', 'rye', 'spice', 'pumpernickel', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Blue Ox Malthouse',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Unmalted barley roasted black. Coffee, a dry bite and the tan head that goes with a dry Irish stout.',
    tags: ['roasted', 'barley', 'stout', 'irish', 'craft-malt', 'maine'],
    url: 'https://www.blueoxmalthouse.com/merch-house/roasted-barley',
  }),
  craftMalt({
    id: 'blue-ox-roasted-oats',
    name: 'Roasted Oats',
    maker: 'Blue Ox Malthouse',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 5,
    colorLovibond: 250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'An unusual one: oats taken to a dark roast, which keeps the silky body oats bring while adding cocoa and toast. Good in an oatmeal stout that should stay soft.',
    tags: ['roasted', 'oats', 'stout', 'smooth', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-roasted-wheat',
    name: 'Roasted Wheat',
    maker: 'Blue Ox Malthouse',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 425,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Huskless, so it roasts smoother than barley: dark chocolate and dry toast without the astringency.',
    tags: ['roasted', 'wheat', 'stout', 'smooth', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-golden-triticale',
    name: 'Golden Triticale',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 15,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      "Triticale kilned to gold: the rye-wheat hybrid's bready spice with a light toast over it. Distinctive and rarely available anywhere else.",
    tags: ['triticale', 'kilned', 'hybrid', 'bready', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-cherrywood-smoked-pale',
    name: 'Cherrywood Smoked Pale',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Pale malt smoked over cherry: sweeter and fruitier than beech, and gentle enough to be most of a grist.',
    tags: ['smoked', 'cherrywood', 'base', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-alderwood-smoked-pale',
    name: 'Alderwood Smoked Pale',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Alder smoke, which is what the Pacific Northwest smokes salmon over: clean, slightly sweet and less phenolic than beech.',
    tags: ['smoked', 'alderwood', 'base', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-applewood-smoked-pale',
    name: 'Applewood Smoked Pale',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'The mildest of the house smokes: apple wood is sweet and light, so this seasons a beer rather than defining it.',
    tags: ['smoked', 'applewood', 'base', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-maplewood-smoked-triticale',
    name: 'Maplewood Smoked Triticale',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      "Maple smoke on triticale rather than barley, which is about as Maine as a malt gets. Sweet smoke over the hybrid's bready spice.",
    tags: ['smoked', 'maplewood', 'triticale', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-chit',
    name: 'Chit Malt',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Barley stopped days into germination. Protein for foam and body, almost no extract and no flavor to speak of.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'maine'],
    url: 'https://www.blueoxmalthouse.com/merch-house/chit-malt',
  }),
  craftMalt({
    id: 'blue-ox-rye',
    name: 'Rye Malt',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Organic Maine rye, malted: peppery, dry and slick in the body. Rice hulls above a quarter of the grist.',
    tags: ['rye', 'organic', 'spice', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-triticale',
    name: 'Triticale Malt',
    maker: 'Blue Ox Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      "Organic triticale, the rye-wheat hybrid: rye's spice with wheat's body and a better lauter than either.",
    tags: ['triticale', 'organic', 'hybrid', 'craft-malt', 'maine'],
  }),
  craftMalt({
    id: 'blue-ox-munich-wheat',
    name: 'Munich Wheat',
    maker: 'Blue Ox Malthouse',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 10,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Wheat taken to Munich color: bread crust and a rounder sweetness than pale wheat, with the head retention wheat always brings.',
    tags: ['wheat', 'munich', 'kilned', 'dark-wheat', 'craft-malt', 'maine'],
  }),

  // ---------------------------------------------------------------------------
  // CNC Malt — Fenelton, Pennsylvania
  //
  // CNC (Custom 'N Craft) Malting Company floor-malt Butler County grain in a
  // former township school. Their shop lists each malt on its own public page
  // with a short paragraph of copy; none of them carries an analysis, so every
  // number below stays class-typical and `representative`.
  // ---------------------------------------------------------------------------
  craftMalt({
    // Sold as "CNC Floor-Malted Pilsner Malt"; the id predates the fuller name.
    // Nothing CNC publish claims organic certification or heritage varieties,
    // so the description no longer does either.
    id: 'cnc-pilsner',
    name: 'Floor-Malted Pilsner Malt',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted Western Pennsylvania pilsner, enzyme-rich and high in extract. CNC sell it as a malt that can carry the whole grain bill, which makes it the lager base and the quiet floor under a hop-forward pale beer.',
    tags: ['base', 'pilsner', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-floor-malted-pilsner-malt/',
  }),
  craftMalt({
    id: 'cnc-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Well-modified floor-malted pale built for English-style ales: bread-toast malt flavor with enough enzyme to convert a modest adjunct fraction. The house everyday ale malt.',
    tags: ['base', 'pale-ale', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-pale-ale-malt-2/',
  }),
  craftMalt({
    id: 'cnc-american-ale',
    name: 'American Ale Malt',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The 2-row workhorse: deeper malt flavor than the pilsner, still pale enough for IPA and American lager. Floor-malted from locally sourced Pennsylvania barley.',
    tags: ['base', 'two-row', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-american-pale-malt-2/',
  }),
  craftMalt({
    id: 'cnc-vienna',
    name: 'Vienna Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted Vienna with the slightly toasty, nutty flavor CNC put on the label, and enough diastatic power to convert a whole grist. Built for märzen and as a 15–40 % seasoning in amber ales.',
    tags: ['kilned', 'vienna', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/vienna-craft-malt/',
  }),
  craftMalt({
    id: 'cnc-light-munich',
    name: 'Light Munich Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lower in enzyme than the base malts but, CNC say, still enough to convert a mash at 100 % of the grain bill. Bread-crust depth for märzen, Oktoberfest and maibock.',
    tags: ['kilned', 'munich', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-light-munich-malt/',
  }),
  craftMalt({
    // CNC list this as "Pennsylvania Malted Rye" and shelve it as "CNC Malted
    // Rye"; the id keeps the older word order.
    id: 'cnc-pennsylvania-rye',
    name: 'Pennsylvania Malted Rye',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Floor-malted Pennsylvania rye, spicy enough to add complexity anywhere from a pale lager to a dark ale. Sticky above about 20 %, so rice hulls are worth the trouble once rye is a real fraction of the mash.',
    tags: ['base', 'rye', 'craft-malt', 'pennsylvania', 'floor-malted', 'spicy'],
    url: 'https://cncmalt.com/product/cnc-malted-rye/',
  }),
  craftMalt({
    id: 'cnc-distillers',
    name: 'Distillers Malt',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned cool to protect the enzyme, so the diastatic power is high enough to convert a large adjunct charge. Made for the still, and useful in any beer whose grist is short of enzyme.',
    tags: ['base', 'distilling', 'high-dp', 'enzyme', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-distillers-malt-2/',
  }),
  craftMalt({
    id: 'cnc-dark-munich',
    name: 'Dark Munich Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Nutty and toasty with a coffee note at the finish. For Oktoberfest and maibock, and for porters that want malt depth rather than roast.',
    tags: [
      'kilned',
      'munich',
      'dark',
      'oktoberfest',
      'porter',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
    url: 'https://cncmalt.com/product/cnc-dark-munich-malt/',
  }),
  craftMalt({
    // CNC sell one made-to-order "Crystal Malt" across 20–100 °L rather than
    // numbered grades; this row models the 40 °L grade the id names.
    id: 'cnc-crystal-40',
    name: 'Crystal 40',
    maker: 'CNC Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'One grade of a made-to-order crystal range that runs 20 to 100 °L, stewed in small lots to keep the flavor. Caramel and toffee, and the grade that suits most ambers and pale ales.',
    tags: ['crystal', 'caramel', 'amber', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-crystal-malt/',
  }),
  craftMalt({
    id: 'cnc-biscuit',
    name: 'Biscuit Malt',
    maker: 'CNC Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Bread crust, nut and toasted biscuit, with no enzyme left. Five to ten percent gives a pale beer a bakery depth it cannot get from a crystal.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-bicuit-malt/',
  }),
  craftMalt({
    id: 'cnc-chocolate-floor-malt',
    name: 'Chocolate Floor Malt',
    maker: 'CNC Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Floor-malted barley taken to chocolate color: dark, rich coffee and cocoa with no enzyme left. Three to eight percent carries the color and most of the roast of a porter or a stout.',
    tags: ['roasted', 'chocolate', 'porter', 'stout', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-chocolate-floor-malt/',
  }),
  // The id says `smoked`; the sack says "PA Smoked Malt".
  //
  // Diastatic power stays at a base-malt figure, and the reasoning has been
  // rewritten once because the evidence changed under it. An audit set it to 0
  // on the strength of "a specialty malt that lacks enzymatic potential"; a
  // later note argued that sentence was CNC's copy for Biscuit Malt rather than
  // this one, and restored 50. Re-read on 2026-09-21, CNC's smoked page does
  // carry that sentence — so the second note was wrong about where the words
  // are, and right about what they are worth. The same sentence appears
  // verbatim on Biscuit and on Chocolate Floor Malt, opening all three
  // descriptions before each adds its own flavour clause. It is boilerplate
  // CNC paste onto every specialty malt, not a claim measured about this one,
  // and a smoked malt is normally a base malt that has met smoke rather than a
  // roast, which is why Colorado's peated malt is a pale base too. CNC publish
  // no analysis, so 50 is class-typical and `representative`, like every other
  // figure in this row.
  //
  // The wood list is theirs and it moves: the page read "Apple-wood, pear,
  // cherry or other smoke flavor available" when this row was written and reads
  // "Applewood, Oak, Pear" now. The description below follows the current page
  // and stays vague about the rest, because made-to-order means the list is a
  // sample rather than a menu.
  craftMalt({
    id: 'cnc-smoked',
    name: 'PA Smoked Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Floor-malted and smoked to order, over applewood, pear or oak as standard and other woods on request. The fruit woods give a sweeter, rounder smoke than the beech a German rauchbier is built on, and oak is the drier option. Enough enzyme to carry its own weight in the mash.',
    tags: [
      'smoked',
      'applewood',
      'pear',
      'oak',
      'specialty',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
    url: 'https://cncmalt.com/product/cnc-smoked-malt/',
  }),
  // The three named smokes below, and why they are rows rather than a note on
  // the one above.
  //
  // CNC's shop lists a single `CNC PA Smoked Malt` "smoked to order", the same
  // way it lists a single made-to-order Crystal across 20-100 °L. We already
  // model one grade of that crystal range as its own row, and these are the
  // same move: specific pairings of grain and wood that CNC make to order and
  // do not shelve separately, so there is no product page to link and no
  // analysis to read. Every figure is class-typical and `representative`, and
  // the smoke-and-grain facts come from the brewer who bought them rather than
  // from a spec sheet.
  //
  // What separates them is **when the smoke meets the grain**, which is the
  // only thing about a smoked malt that changes its numbers. Cold smoke is
  // applied to finished malt below the temperature that would kiln it further,
  // so it adds aroma and leaves the colour and the enzyme where the malting
  // left them. Smoke made part of the kilning is the older method a rauchmalz
  // uses, and the fire that carries it is also drying the grain: the malt comes
  // out darker, smokier and with less enzyme than the same grain cold-smoked.
  // That is why the pear wheat below is three times the colour of the oak one
  // despite being the same grain from the same floor.
  craftMalt({
    // Kilned over the smoke rather than smoked afterwards, which is where the
    // colour comes from. 8 °L is the brewer's "darker and very smoky" read as a
    // dark-wheat figure, not a published one: it puts the malt a shade under
    // Weyermann's Dark Wheat and well above the cold-smoked wheat below. The
    // enzyme is cut to match the longer, hotter kiln but not to zero, because a
    // wheat malt starts high and a smoke kiln is not a roaster.
    id: 'cnc-pear-smoked-wheat',
    name: 'Pear Smoked Wheat Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Wheat kilned over pear smoke rather than smoked cold afterwards, so the fire that flavors it also darkens it. The most assertive of the house smokes: sweet orchard wood over bread and a color closer to a dark wheat than a pale one. Ten to twenty percent is a smoked wheat beer; thirty is a statement.',
    tags: [
      'smoked',
      'pear',
      'kiln-smoked',
      'wheat',
      'dark-wheat',
      'specialty',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
  }),
  craftMalt({
    // Cold-smoked, so the numbers are CNC's own barley with an aroma on it:
    // colour and enzyme sit with the Blue Ox cold smokes rather than with the
    // pear wheat above.
    id: 'cnc-peach-smoked-barley',
    name: 'Peach Smoked Barley',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Finished barley malt cold-smoked over peach wood, so it picks up the smoke without picking up color or losing its enzyme. Peach is the gentlest of the stone fruits: sweeter and rounder than beech, and mild enough to be half a grist without taking the beer over.',
    tags: [
      'smoked',
      'peach',
      'cold-smoked',
      'base',
      'specialty',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
  }),
  craftMalt({
    // Cold-smoked wheat, so it keeps the wheat malt's colour and most of its
    // enzyme. The pairing Weyermann made famous, on Pennsylvania grain.
    id: 'cnc-oak-smoked-wheat',
    name: 'Oak Smoked Wheat Malt',
    maker: 'CNC Malt',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted wheat cold-smoked over oak, which is the drier and more tannic of the house smokes: woodsmoke and a faint whisky-barrel note rather than orchard sweetness. Pale enough to smoke a weizen or a grodziskie without darkening it, and it still brings wheat’s foam.',
    tags: [
      'smoked',
      'oak',
      'cold-smoked',
      'wheat',
      'grodziskie',
      'specialty',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
  }),
  craftMalt({
    id: 'cnc-pennsylvania-wheat',
    name: 'Pennsylvania Malted Wheat',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 5.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Pennsylvania-grown red wheat, floor-malted: strong foam formation and retention, and the bready body a weizen or a Berliner Weisse is built on.',
    tags: ['wheat', 'red-wheat', 'weizen', 'foam', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-malted-wheat/',
  }),
  craftMalt({
    // Genuinely CNC's own — they list "CNC Bloody Butcher Malted Corn" and a
    // raw Bloody Butcher alongside it. CNC publish no tasting note for it, so
    // the description sticks to what the grain and the process are.
    id: 'cnc-bloody-butcher-corn',
    name: 'Bloody Butcher Malted Corn',
    maker: 'CNC Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'The red heirloom dent corn, malted rather than flaked, so it mashes in with the grist instead of needing a cereal cook. CNC sell the raw kernel too; the malted version is the one for a mash tun.',
    tags: [
      'corn',
      'maize',
      'heirloom',
      'adjunct',
      'gluten-free',
      'craft-malt',
      'pennsylvania',
      'floor-malted',
    ],
    url: 'https://cncmalt.com/product/cnc-malted-corn/',
  }),
  craftMalt({
    id: 'cnc-malted-oats',
    name: 'Malted Oats',
    maker: 'CNC Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Floor-malted Pennsylvania oats with a rich granola flavor and the creamy, velvety mouthfeel a hazy IPA is after. Unlike flaked oats they bring a little enzyme of their own.',
    tags: ['oat', 'malted-oats', 'hazy', 'mouthfeel', 'craft-malt', 'pennsylvania', 'floor-malted'],
    url: 'https://cncmalt.com/product/cnc-malted-oats/',
  }),

  // ---------------------------------------------------------------------------
  // Deer Creek Malthouse — Glen Mills, Pennsylvania
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'deer-creek-colonial-pils',
    name: 'Colonial Pils',
    maker: 'Deer Creek Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 135,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://www.deercreekmalt.com/product/colonial-pils-malt/',
    provenance: 'spec-sheet',
    description:
      'The house 2-row pilsner, floor-germinated for a doughy, fresh-hay sweetness. High diastatic power for a craft malt, so it converts unmalted grain readily and can be the whole grist of a saison, pilsner or IPA.',
    tags: ['base', 'pilsner', 'craft-malt', 'pennsylvania', 'floor-malted', 'high-enzyme'],
  }),
  craftMalt({
    id: 'deer-creek-keystone-pale-ale',
    name: 'Keystone Pale Ale',
    maker: 'Deer Creek Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 3.5,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    url: 'https://www.deercreekmalt.com/product/keystone-pale-ale-malt/',
    provenance: 'spec-sheet',
    description:
      'UK-style pale ale malt from Pennsylvania 2-row, kilned a shade darker than the pilsner. Bready and self-converting, the default base for bitters, pale ales and malt-forward IPAs.',
    tags: ['base', 'pale-ale', 'craft-malt', 'pennsylvania', 'floor-malted'],
  }),
  craftMalt({
    id: 'deer-creek-white-tail-pale',
    name: 'White Tail Pale',
    maker: 'Deer Creek Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Six-row American pale with the extra enzyme that pre-Prohibition adjunct lagers were built on. A little grainier than the 2-row, and it will convert a serious corn or rice fraction.',
    tags: ['base', 'six-row', 'craft-malt', 'pennsylvania', 'high-enzyme', 'pre-prohibition'],
  }),
  craftMalt({
    id: 'deer-creek-sweet-liberty',
    name: 'Sweet Liberty',
    maker: 'Deer Creek Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Vienna-style 2-row with toasted-cracker sweetness. Still a base malt, so it can carry a festbier or sit at 10–30 % under hops that need a warmer malt floor.',
    tags: ['kilned', 'vienna', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'deer-creek-dutch',
    name: 'Dutch Malt',
    maker: 'Deer Creek Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.5,
    moisturePercent: 3.6,
    colorLovibond: 10,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 50,
    needsMashing: true,
    url: 'https://www.deercreekmalt.com/product/dutch-malt/',
    provenance: 'spec-sheet',
    description:
      'Munich-style 2-row kilned on the Dutch protocol, giving bread-crust depth and an amber tone. Enough enzyme to convert itself; 15–40 % is the usual seasoning in bock and amber lager.',
    tags: ['kilned', 'munich', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'deer-creek-double-dutch',
    name: 'Double Dutch',
    maker: 'Deer Creek Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Dutch taken twice through the kiln, at double the color: deep bread crust, toffee and a dark-honey sweetness. A color and depth malt for amber and brown beers rather than a base.',
    tags: ['kilned', 'munich', 'toffee', 'amber', 'craft-malt', 'pennsylvania'],
    url: 'https://www.deercreekmalt.com/product/double-dutch-malt/',
  }),
  craftMalt({
    id: 'deer-creek-pale-wheat',
    name: 'Pale Wheat Malt',
    maker: 'Deer Creek Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    url: 'https://www.deercreekmalt.com/product/pale-wheat-malt/',
    provenance: 'spec-sheet',
    description:
      'Lightly kilned wheat malt, high extract and high protein. Builds the haze, foam and soft body of weissbier and hazy IPA; plan on rice hulls past about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'pennsylvania', 'head-retention'],
  }),
  craftMalt({
    id: 'deer-creek-pale-rye',
    name: 'Pale Rye Malt',
    maker: 'Deer Creek Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    url: 'https://www.deercreekmalt.com/product/pale-rye-malt/',
    provenance: 'spec-sheet',
    description:
      'Lightly kilned rye with a spicy cracker bite and very high extract. Beta-glucans make the mash sticky above about 20 %, so rice hulls are not optional at rye-beer rates.',
    tags: ['base', 'rye', 'craft-malt', 'pennsylvania', 'spicy'],
  }),
  craftMalt({
    id: 'deer-creek-smoked',
    name: 'Smoked Malt',
    maker: 'Deer Creek Malthouse',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-germinated base malt smoked to order. Keeps enough enzyme to convert itself, so it can be the whole grist of a rauchbier or a measured accent in porter and smoked lager.',
    tags: ['smoked', 'craft-malt', 'pennsylvania', 'floor-malted', 'base'],
  }),

  // ---------------------------------------------------------------------------
  // Double Eagle Malt — Huntingdon Valley, Pennsylvania
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'double-eagle-pennypack-pils',
    name: 'Pennypack Pils',
    maker: 'Double Eagle Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lightly kilned 2-row pilsner from Mid-Atlantic farms, tasting of dry crackled hay. A quiet, versatile base that lets hops, yeast or a single-malt lager speak.',
    tags: ['base', 'pilsner', 'craft-malt', 'pennsylvania', 'local'],
  }),
  craftMalt({
    id: 'double-eagle-rustic-ale',
    name: 'Rustic Ale',
    maker: 'Double Eagle Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Pale ale malt kilned a step darker than commodity 2-row, with cereal and lightly nutty aromatics. The house signature base for pale ale, IPA and malt-forward lager.',
    tags: ['base', 'pale-ale', 'craft-malt', 'pennsylvania', 'local'],
  }),
  craftMalt({
    id: 'double-eagle-legacy-wheat',
    name: 'Legacy Wheat',
    maker: 'Double Eagle Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Pale wheat from Pennsylvania hard red winter wheat. Light, smooth and slightly sweet, it promotes yeast activity, mouthfeel and a lasting head in wheat beers and hazy ales.',
    tags: ['base', 'wheat', 'craft-malt', 'pennsylvania', 'head-retention'],
  }),
  craftMalt({
    id: 'double-eagle-heritage-rye',
    name: 'Heritage Rye',
    maker: 'Double Eagle Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Pale rye from Pennsylvania winter rye, adding depth and a distinctive spiciness. Keep the mash open with rice hulls once rye is more than a seasoning.',
    tags: ['base', 'rye', 'craft-malt', 'pennsylvania', 'spicy'],
  }),
  craftMalt({
    id: 'double-eagle-vienna',
    name: 'Vienna Malt',
    maker: 'Double Eagle Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Sweet 2-row Vienna with lightly toasted bread flavor. Works as the whole grist of a golden lager or as an aromatic addition under hops.',
    tags: ['kilned', 'vienna', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-munich',
    name: 'Munich Malt',
    maker: 'Double Eagle Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Traditional 2-row Munich: chewy bread, light toast and crackers with an amber color. A deep, slightly sweet malt character for festbier, bock and amber ale.',
    tags: ['kilned', 'munich', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-cocoa',
    name: 'Cocoa',
    maker: 'Double Eagle Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 225,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Signature chocolate malt roasted a shade lighter than commercial cocoa malts, so the roast stays smooth rather than bitter or astringent. Two to eight percent in porter and brown ale is plenty; also offered on wheat and rye.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-heirloom-corn',
    name: 'Malted Corn',
    maker: 'Double Eagle Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 2,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Pale malt from Pennsylvania-grown corn, malted rather than flaked. A flavorful stand-in for flaked maize in pre-Prohibition lager, cream ale and corn whiskey mash bills.',
    tags: ['adjunct', 'corn', 'malted-corn', 'craft-malt', 'pennsylvania', 'pre-prohibition'],
  }),

  craftMalt({
    id: 'double-eagle-pale-oat',
    name: 'Pale Oat',
    maker: 'Double Eagle Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Pennsylvania oats malted pale, and a rich adjunct because of the oil in them. Contributes mouthfeel and a stable haze to New England IPA, and a soft cereal weight to stout and porter.',
    tags: ['adjunct', 'oats', 'haze', 'mouthfeel', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-pale-spelt',
    name: 'Pale Spelt',
    maker: 'Double Eagle Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Organically grown Pennsylvania spelt, malted in the husk. Nuttier and rounder than wheat, and the husk it keeps makes it lauter far better than a wheat malt at the same fraction of the grist.',
    tags: ['base', 'spelt', 'organic', 'ancient-grain', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-symphony',
    name: 'Symphony',
    maker: 'Double Eagle Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 9.4,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Composed like a Vienna but kilned to the color of a Munich, so it brings toast and bread crust without the heavier sweetness. The base a Vienna lager, Czech amber, American amber or English mild is built on.',
    tags: ['kilned', 'vienna', 'munich', 'amber', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-aromunich',
    name: 'Aromunich',
    maker: 'Double Eagle Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 4,
    colorLovibond: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'An aromatic melanoidin malt: deep cracker flavor, a light sweetness and amber color, with none of the harsh edge a roast of that depth would bring. Five to ten percent changes the beer.',
    tags: ['kilned', 'aromatic', 'melanoidin', 'amber', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-dextrin',
    name: 'Dextrin',
    maker: 'Double Eagle Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A light dextrin malt from barley grown on Pennsylvania family farms. Body, mouthfeel and head retention with no color and no caramel flavor, which is what a pale beer needs when a crystal would show.',
    tags: ['dextrin', 'body', 'head-retention', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-caramel-7',
    name: 'Caramel 7',
    maker: 'Double Eagle Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 5.5,
    colorLovibond: 7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The lightest caramel in the house range, and paler than most barley crystals get: light honey and a faint biscuit sweetness with almost no color. Adds body and foam to beers too pale for a darker crystal.',
    tags: ['crystal', 'caramel', 'light', 'body', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-caramel-25',
    name: 'Caramel 25',
    maker: 'Double Eagle Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'A medium-color kilned caramel that adds residual sweetness and melanoidins. The everyday crystal for pale ales and ambers at five to ten percent.',
    tags: ['crystal', 'caramel', 'amber', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-caramel-90',
    name: 'Caramel 90',
    maker: 'Double Eagle Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'The medium-dark crystal: toffee and toasted sugar over the first of the dried fruit. Where a brown ale, a bitter or an old ale gets its depth of sweetness.',
    tags: ['crystal', 'caramel', 'toffee', 'dark', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-cocoa-dark',
    name: 'Cocoa Dark',
    maker: 'Double Eagle Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Traditional dark chocolate malt, rich and roasty where the lighter Cocoa is smooth. Three to eight percent carries a stout or a robust porter, and it is offered on Pennsylvania barley, wheat or rye.',
    tags: ['roasted', 'chocolate', 'dark', 'stout', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-black-magic',
    name: 'Black Magic',
    maker: 'Double Eagle Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 550,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    description:
      'Black malt from Pennsylvania hard red winter wheat. Huskless, so it gives the color of a black malt with far less of the astringent bite; one or two percent is usually all a recipe wants.',
    tags: ['roasted', 'black', 'wheat', 'color', 'craft-malt', 'pennsylvania'],
  }),
  craftMalt({
    id: 'double-eagle-roast-barley',
    name: 'Roast Barley',
    maker: 'Double Eagle Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Unmalted barley taken to a dark roast: sharp coffee, a dry finish and the tan head a dry Irish stout is judged on.',
    tags: ['roasted', 'barley', 'stout', 'irish', 'craft-malt', 'pennsylvania'],
  }),

  // ---------------------------------------------------------------------------
  // Hudson Valley Malt — Germantown, New York
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'hudson-valley-germantown-pils',
    name: 'Germantown Pils',
    maker: 'Hudson Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted pilsner from 100 % New York-grown barley. Pale, clean and lightly grainy, the house lager base and a quiet canvas for farmhouse ales.',
    tags: ['base', 'pilsner', 'craft-malt', 'new-york', 'floor-malted', 'estate'],
  }),
  craftMalt({
    id: 'hudson-valley-hudson-pale',
    name: 'Hudson Pale 2-Row',
    maker: 'Hudson Valley Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Floor-malted New York 2-row, a step richer than the pilsner. Everyday pale base for ales and hop-forward lagers brewed from Hudson Valley grain.',
    tags: ['base', 'two-row', 'craft-malt', 'new-york', 'floor-malted'],
  }),
  craftMalt({
    id: 'hudson-valley-lady-fathauer',
    name: 'Lady Fathauer',
    maker: 'Hudson Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Light Munich named for a farm neighbor, kilned to bread-crust color with enough enzyme left to convert itself. The malt spine of festbier and darker farmhouse ales.',
    tags: ['kilned', 'munich', 'craft-malt', 'new-york', 'floor-malted'],
  }),
  craftMalt({
    id: 'hudson-valley-mr-toastman',
    name: 'Mr. Toastman',
    maker: 'Hudson Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Vienna-style floor malt with toasted-cracker sweetness. Self-converting, so it can be the whole grist of a golden lager or a 15–30 % seasoning.',
    tags: ['kilned', 'vienna', 'craft-malt', 'new-york', 'floor-malted'],
  }),
  craftMalt({
    id: 'hudson-valley-wheat',
    name: 'Wheat Malt',
    maker: 'Hudson Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Floor-malted New York soft red winter wheat. High protein for foam and haze; rice hulls keep the lauter moving once wheat is a large fraction of the mash.',
    tags: ['base', 'wheat', 'craft-malt', 'new-york', 'floor-malted', 'head-retention'],
  }),
  craftMalt({
    id: 'hudson-valley-a-little-spice',
    name: 'A Little Spice',
    maker: 'Hudson Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 4.2,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Floor-malted rye with the peppery bite the name promises. Use as a seasoning in rye pale ale or at higher rates in roggenbier, with hulls to keep the mash from sealing.',
    tags: ['base', 'rye', 'craft-malt', 'new-york', 'floor-malted', 'spicy'],
  }),
  craftMalt({
    id: 'hudson-valley-dennys-dextrin',
    name: "Denny's Dextrin",
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Pale dextrin malt for body and foam without color or caramel flavor. Three to eight percent thickens a beer too pale to hide a crystal malt.',
    tags: ['dextrin', 'craft-malt', 'new-york', 'body', 'head-retention'],
  }),
  craftMalt({
    id: 'hudson-valley-apple-smoked',
    name: 'Apple Smoked 2-Row',
    maker: 'Hudson Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 5.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'New York 2-row smoked over applewood. Sweeter and fruitier than beech, it can carry a smoked ale on its own or season a porter at 5–20 %.',
    tags: ['smoked', 'apple', 'craft-malt', 'new-york', 'floor-malted', 'base'],
  }),

  craftMalt({
    id: 'hudson-valley-mr-spaeth',
    name: 'Mr. Spaeth',
    maker: 'Hudson Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'The house dark Munich: bread crust, toffee and dark honey over a deep amber color. Just enough enzyme left to be a large part of a grist, and where a dunkel or a bock gets most of its malt character.',
    tags: ['kilned', 'munich', 'dark', 'dunkel', 'floor-malted', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-spelt',
    name: 'Spelt Malt',
    maker: 'Hudson Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Floor-malted spelt, husk and all. Softer and nuttier than wheat with a rustic bread flavor, and the husk keeps the lauter moving at rates that would seal a wheat mash.',
    tags: ['base', 'spelt', 'ancient-grain', 'floor-malted', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-oat',
    name: 'Oat Malt',
    maker: 'Hudson Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 3.8,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'New York oats taken through a full floor malting. Creamy weight and a granola note for hazy IPA and oatmeal stout, with less of the gumminess flakes bring to the mash.',
    tags: ['adjunct', 'oats', 'mouthfeel', 'floor-malted', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-kilned-caramel-20',
    name: 'Kilned Caramel C-20',
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The palest of the house caramels: light honey and a touch of biscuit, adding body and foam without moving the color much. The one to reach for in a golden ale or a helles.',
    tags: ['crystal', 'caramel', 'light', 'body', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-kilned-caramel-40',
    name: 'Kilned Caramel C-40',
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5.5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Gold to light amber with clean caramel sweetness. The everyday crystal for pale ales and ambers, and the grade most recipes mean when they just say "crystal".',
    tags: ['crystal', 'caramel', 'amber', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-kilned-caramel-60',
    name: 'Kilned Caramel C-60',
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Dark toffee and the first of the dried fruit, over a copper cast. Where an English bitter or a brown ale gets its sweetness; much past ten percent and the beer turns cloying.',
    tags: ['crystal', 'caramel', 'toffee', 'english', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-kilned-caramel-90',
    name: 'Kilned Caramel C-90',
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Raisin and dark treacle over a firm caramel base. A small addition does the work of a much larger one, so ten percent is plenty in almost any beer.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-kilned-caramel-120',
    name: 'Kilned Caramel C-120',
    maker: 'Hudson Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The dark end of the caramel range: prune, burnt sugar and a faintly roasty edge. For old ales, doppelbocks and anything that wants ruby depth without actual roast.',
    tags: ['crystal', 'caramel', 'dark', 'prune', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-chocolate',
    name: 'Chocolate Malt',
    maker: 'Hudson Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 360,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Cocoa, coffee and a dry roast bite. Three to eight percent builds the color and most of the roast character of a porter or a stout.',
    tags: ['roasted', 'chocolate', 'porter', 'stout', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Hudson Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Unmalted barley roasted black: sharp coffee, an astringent dry finish and the tan head that goes with a dry Irish stout.',
    tags: ['roasted', 'barley', 'stout', 'irish', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-roasted-oat-malt',
    name: 'Roasted Oat Malt',
    maker: 'Hudson Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Oat malt given a light roast rather than a dark one, so it lands nearer Vienna color than chocolate. Toasted cereal and a nutty edge with the silky body oats always bring.',
    tags: ['roasted', 'oats', 'toasted', 'mouthfeel', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-flaked-oats',
    name: 'Flaked Oats',
    maker: 'Hudson Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 9,
    colorLovibond: 1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Rolled and pregelatinised oats, added for body rather than flavor. They thicken a stout and hold the haze in a New England IPA; rice hulls earn their keep above about twenty percent.',
    tags: ['adjunct', 'oats', 'flaked', 'body', 'mouthfeel', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-flaked-wheat',
    name: 'Flaked Wheat',
    maker: 'Hudson Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 9,
    colorLovibond: 1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Pregelatinised unmalted wheat: protein for foam and haze with no husk and no enzyme of its own. The traditional route to a witbier, and the quickest way to firm up a hazy IPA.',
    tags: ['adjunct', 'wheat', 'flaked', 'haze', 'head-retention', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-flaked-corn',
    name: 'Flaked Corn',
    maker: 'Hudson Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 9,
    colorLovibond: 1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Pregelatinised maize, which lightens body and color and dries the finish without a cereal cooker. The adjunct behind cream ale and pre-Prohibition lager.',
    tags: ['adjunct', 'corn', 'flaked', 'pre-prohibition', 'craft-malt', 'new-york'],
  }),
  craftMalt({
    id: 'hudson-valley-flaked-rye',
    name: 'Flaked Rye',
    maker: 'Hudson Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 9,
    colorLovibond: 1.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Pregelatinised rye, mash-ready and unmalted. Keeps the grainy, spicy cracker character of rye and adds a slick body; rice hulls are not optional at rye-beer rates.',
    tags: ['adjunct', 'rye', 'flaked', 'spicy', 'craft-malt', 'new-york'],
  }),

  // ---------------------------------------------------------------------------
  // Maine Malt House — Mapleton, Maine
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'maine-malt-mapleton-pale',
    name: 'Mapleton Pale',
    maker: 'Maine Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house American 2-row from a multi-generational Aroostook County family: clean and mildly malty with the grape-nut note the maltster puts at the front of it. Carries a whole ale grist on its own.',
    tags: ['base', 'ale', 'craft-malt', 'maine', 'estate', 'local'],
    url: 'https://www.mainemalt.com/product-page/mapleton-pale',
  }),
  craftMalt({
    id: 'maine-malt-mapleton-gold',
    name: 'Mapleton Gold',
    maker: 'Maine Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      "Kilned a shade past Mapleton Pale and offered as the farm's answer to Golden Promise: soft, biscuity and a little sweeter than an American 2-row. A whole-grist base for bitters, milds and Scottish ales.",
    tags: ['base', 'pale', 'golden-promise', 'craft-malt', 'maine', 'estate', 'local'],
    url: 'https://www.mainemalt.com/product-page/mapleton-gold',
  }),
  craftMalt({
    id: 'maine-malt-munich-i',
    name: 'Munich I',
    maker: 'Maine Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 6,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Light Munich from Aroostook barley, toasted to bread-crust color with enough enzyme to convert itself. The malt depth in festbier, dunkel and darker Maine ales.',
    tags: ['kilned', 'munich', 'craft-malt', 'maine', 'estate'],
    url: 'https://www.mainemalt.com/product-page/munich-i',
  }),
  craftMalt({
    id: 'maine-malt-red-wheat',
    name: 'Red Wheat Malt',
    maker: 'Maine Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted red wheat grown on the Buck family farm, tasting of wheat flour with a creamy malty sweetness. Builds body and a denser head; rice hulls help once it is a large fraction of the mash.',
    tags: ['base', 'wheat', 'craft-malt', 'maine', 'estate', 'head-retention'],
    url: 'https://www.mainemalt.com/product-page/red-wheat-malt',
  }),
  craftMalt({
    id: 'maine-malt-oat',
    name: 'Oat Malt',
    maker: 'Maine Malt House',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 2.2,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Estate-grown oats taken through a full malting. Creamy weight for hazy IPA and stout with less mash gumminess than flakes, and a rounder cereal flavor.',
    tags: ['adjunct', 'oats', 'craft-malt', 'maine', 'estate', 'mouthfeel'],
    url: 'https://www.mainemalt.com/product-page/oat-malt',
  }),
  craftMalt({
    id: 'maine-malt-rye',
    name: 'Rye Malt',
    maker: 'Maine Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Aroostook rye malted on the farm. Spicy, slightly peppery and sticky in the mash — rice hulls are the difference between a rye beer and a stuck sparge.',
    tags: ['base', 'rye', 'craft-malt', 'maine', 'estate', 'spicy'],
    url: 'https://www.mainemalt.com/product-page/rye-malt',
  }),

  craftMalt({
    id: 'maine-malt-pilsen',
    name: 'Pilsen',
    maker: 'Maine Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The palest malt on the farm, kilned for lagers and light ales: clean, with a subtle malty sweetness and nothing else in the way. A quiet floor under hops, yeast or a single-malt pilsner.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'maine', 'estate'],
    url: 'https://www.mainemalt.com/product-page/pilsen',
  }),
  craftMalt({
    id: 'maine-malt-vienna',
    name: 'Vienna',
    maker: 'Maine Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Aroostook barley kilned to a mild malty sweetness with a light toast behind it. Still self-converting, so it can be the whole grist of a Vienna lager or a ten to thirty percent warming of a pale ale.',
    tags: ['kilned', 'vienna', 'craft-malt', 'maine', 'estate'],
    url: 'https://www.mainemalt.com/product-page/vienna',
  }),
  craftMalt({
    id: 'maine-malt-munich-ii',
    name: 'Munich II',
    maker: 'Maine Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 9,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'The darker of the two house Munichs, where the malty sweetness turns properly toasted. Color and depth for dunkel, bock and malt-forward amber ales, with enough enzyme left to carry a large share of the mash.',
    tags: ['kilned', 'munich', 'dark', 'bock', 'craft-malt', 'maine', 'estate'],
    url: 'https://www.mainemalt.com/product-page/munich-ii',
  }),

  // ---------------------------------------------------------------------------
  // Murphy & Rude Malting — Charlottesville, Virginia
  //
  // **Merged with Carolina Malt House in June 2026.** M&R's roasting equipment
  // moved to Cleveland, North Carolina, where all their malt is now made, and
  // the two lines sell as one 32-product portfolio. Both brands survive; Jeff
  // still runs sales and product development for the M&R side in Virginia.
  //
  // Which product belongs to which brand is not guesswork. Carolina Malt publish
  // their own fifteen at `carolinamalt.com`, the combined portfolio is at
  // `murphyrudemalting.com/services-9`, and the difference is M&R's. The press
  // release corroborates it exactly: M&R brought "English Pale, five Crystal
  // malts, and eight dry-roasted malts", and subtracting Carolina's list from
  // the combined one leaves an English Pale, five Crystals and eight roasts.
  //
  // Their *list* page is gated behind an email form, which is why these rows sat
  // at six for so long while every other maker got expanded. The individual
  // product pages were public the whole time.
  //
  // Three of those six were Carolina Malt products wearing an M&R label — the
  // same data-migration defect as Carolina/Murphy & Rude in `southeast.ts`, in
  // the opposite direction. See the notes on each.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'murphy-rude-virginia-pils',
    name: 'Virginia Pils',
    maker: 'Murphy & Rude Malting',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Lightly modified and kilned cool so Virginia terroir shows as straw, dry grass and mineral. Pale enough for European lager and happy in a step mash or decoction.',
    tags: ['base', 'pilsner', 'craft-malt', 'virginia', 'local'],
  }),
  // Was a generic "Pale Malt" at 2.5 °L, which is Carolina Gold's colour and
  // Carolina Gold's copy. Murphy & Rude have no Pale Malt; their pale is English
  // Pale, published at 3-3.5 L, and the merger press release names it as one of
  // the three things M&R brought to the combined portfolio. Repointed rather
  // than deleted: base pale to base pale is the same product family, so a
  // cupboard that held this still holds something sensible.
  craftMalt({
    id: 'murphy-rude-pale',
    name: 'English Pale',
    maker: 'Murphy & Rude Malting',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Well-modified pale ale malt taken a little hotter at the end of the cure, which draws out a thread of nutty sweetness: Grape-Nuts, saltines, sunflower seed and honeysuckle. The everyday ale base of the Virginia line.',
    tags: ['base', 'pale-ale', 'english', 'craft-malt', 'virginia', 'local'],
  }),
  craftMalt({
    id: 'murphy-rude-cimmerian-black',
    name: 'Cimmerian Black',
    maker: 'Murphy & Rude Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 525,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    description:
      'Naturally debittered black malt from Virginia hulless barley. Smooth roast color without the husky astringency of chocolate malt, in the Carafa Special vein.',
    tags: ['roasted', 'black', 'debittered', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-crystal-15',
    name: 'Crystal 15',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 15,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The lightest of the five, and pale enough to sweeten a grist without moving its color much. Clean straw and sugar cookie.',
    tags: ['crystal', 'caramel', 'light', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-crystal-40',
    name: 'Crystal 40',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The middle of the range and the one most pale ales want. Brown sugar and vanilla with a stone-fruit edge.',
    tags: ['crystal', 'caramel', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-crystal-60',
    name: 'Crystal 60',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Deeper brown sugar and vanilla than the 40, with the stone fruit turning towards dried apricot. Amber ales and bitters.',
    tags: ['crystal', 'caramel', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-crystal-80',
    name: 'Crystal 80',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Where the caramel starts to read as raisin and dark toffee. Five to ten percent is plenty in a brown ale or a porter.',
    tags: ['crystal', 'caramel', 'dark', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-crystal-150',
    name: 'Crystal 150',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The darkest of the five: charred toffee, dark caramel and dried cherries. A small addition does a lot in stout and old ale.',
    tags: ['crystal', 'caramel', 'dark', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-caramel-dark',
    name: 'CaraMel Dark',
    maker: 'Murphy & Rude Malting',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    description:
      'A kiln-roasted hybrid sitting between a mid-color crystal and a dark Munich, and it tastes like both. Whole-grain cracker and rustic bread crust over brown sugar and candied fruit.',
    tags: ['crystal', 'caramel', 'melanoidin', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-melanoidin',
    name: 'Melanoidin',
    maker: 'Murphy & Rude Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 12,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'The Bavarian trick for malt depth without much color: toasted bran cereal and biscuit with a dark honey sweetness. A tenth of the grist reddens a lager and thickens its middle.',
    tags: ['kilned', 'melanoidin', 'malty', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-biscuit-roast',
    name: 'Biscuit',
    maker: 'Murphy & Rude Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 22,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A dry roast rather than a kilned malt, which is why it lands buttery and baked mid-palate instead of sweet. Good for body, and for rounding off dark malts that are fighting each other.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-belgian-breakfast',
    name: 'Belgian Breakfast',
    maker: 'Murphy & Rude Malting',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 74,
    moisturePercent: 4,
    colorLovibond: 35,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Built on a higher-kilned base than a traditional amber, so it goes deeper than its color suggests. Biscuit bottom, roasted peanut shell, toasted Grape-Nuts, bran flakes and hard pretzel.',
    tags: ['roasted', 'amber', 'toasted', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-americano-roast',
    name: 'Americano Roast',
    maker: 'Murphy & Rude Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 265,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Their pale chocolate, sold as Milk Chocolate until the rename. Mocha and espresso with a touch of burnt toffee, and none of the scorch a full chocolate malt brings.',
    tags: ['roasted', 'chocolate', 'coffee', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-chocolate-wheat',
    name: 'Chocolate Wheat',
    maker: 'Murphy & Rude Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 290,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The roast of their Americano carried on wheat instead of barley, which brings a velvety, almost nitro foam and a silky body with it. At home in anything from a brown ale down.',
    tags: ['roasted', 'wheat', 'chocolate', 'foam', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-bakers-cocoa',
    name: 'Bakers Cocoa',
    maker: 'Murphy & Rude Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 69,
    moisturePercent: 4,
    colorLovibond: 425,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The dark chocolate of the range: smooth and mildly burnt without turning acrid, over a roasted cocoa underbelly. Stout and porter color that will not bite.',
    tags: ['roasted', 'chocolate', 'dark', 'craft-malt', 'virginia'],
  }),

  craftMalt({
    id: 'murphy-rude-rolled-oats',
    name: 'Rolled Oats',
    maker: 'Murphy & Rude Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 70,
    moisturePercent: 9,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Old-fashioned thick-cut oats, the standard hazy IPA lever. Body, softness and silk in any style, and no enzyme of their own, so keep a converting base underneath.',
    tags: ['adjunct', 'oats', 'flaked', 'mouthfeel', 'hazy', 'craft-malt', 'virginia'],
  }),
  craftMalt({
    id: 'murphy-rude-cimmerian-wheat',
    name: 'Cimmerian Wheat',
    maker: 'Murphy & Rude Malting',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 425,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Debittered black wheat malt, a shade lighter than Cimmerian Black with the silk and foam of malted wheat. A Midnight Wheat-style roast for porter, stout and schwarzbier.',
    tags: ['roasted', 'wheat', 'debittered', 'craft-malt', 'virginia'],
  }),
  // Was a second "Malted Corn" duplicating Carolina Malt's, which is the NC
  // yellow dent. The Virginia corn in the portfolio is Buena Vista Yellow, from
  // a single coastal-plain grower, and Pellicle's 2022 profile documents M&R
  // malting heirloom corn. Corn to corn at the same colour, so the id stays.
  craftMalt({
    id: 'murphy-rude-malted-corn',
    name: 'Buena Vista Yellow',
    maker: 'Murphy & Rude Malting',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 2,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      "Open-pollinated heirloom dent corn from one grower on Virginia's coastal plain. Corn flakes, sunflower seed and dry grass, which is the flavor American lager, cream ale and Kentucky common are built on.",
    tags: ['adjunct', 'corn', 'heirloom', 'craft-malt', 'virginia', 'pre-prohibition'],
  }),

  // ---------------------------------------------------------------------------
  // New York Craft Malt — Batavia, New York
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'ny-craft-pale',
    name: 'Erie Canal Pale',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house flagship: clean, crisp New York 2-row with a sweet malty finish. Enough enzyme to convert a real adjunct fraction, and pale enough to sit under anything from a pilsner to an experimental IPA.',
    tags: ['base', 'ale', 'two-row', 'craft-malt', 'new-york', 'local'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/erie-canal-pale',
  }),
  craftMalt({
    id: 'ny-craft-pilsner',
    name: 'Excelsior Pilsner',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.8,
    colorLovibond: 1.8,
    diastaticPowerLintner: 95,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A German-tradition lager base malt from New York barley, pale straw in the kettle with a mild malty sweetness and a note of honey. Delicate enough for a helles and enzyme-rich enough to carry unmalted wheat or oats.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'new-york', 'local'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/excelsior-pilsner',
  }),
  craftMalt({
    id: 'ny-craft-wheat',
    name: 'Seneca Soft White Wheat',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 3.1,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft white wheat grown in central New York and malted for a creamy sweetness with a mellow finish. Adds mouthfeel and holds a head; rice hulls keep the bed open past about a third of the grist.',
    tags: ['base', 'wheat', 'white-wheat', 'craft-malt', 'new-york', 'head-retention'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/seneca-soft-white-wheat',
  }),
  craftMalt({
    id: 'ny-craft-rye',
    name: 'Hudson River Rye',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 4.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted from the heirloom Danko rye, which gives it a rye-crisp cracker flavor rather than a purely peppery one. For rye pale ale, roggenbier and single-malt rye whiskey; hulls are part of the recipe once rye is more than a seasoning.',
    tags: ['base', 'rye', 'danko', 'heirloom', 'craft-malt', 'new-york', 'spicy'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/hudson-river-rye',
  }),

  craftMalt({
    id: 'ny-craft-north-american-pale',
    name: 'North American Pale',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house pale malted from North American rather than New York barley: fuller-bodied and rounder than the estate malts, with a frank malty aroma. Suits English pale ales and anything that wants more body under the hops.',
    tags: ['base', 'pale-ale', 'two-row', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/north-american-pale',
  }),
  craftMalt({
    id: 'ny-craft-north-american-pilsner',
    name: 'North American Pilsner',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Highly modified 2-row kilned to an exceptionally blonde color, giving a clear crisp wort and very little malt flavor of its own. The base for light lagers where the point is what is not there.',
    tags: ['base', 'pilsner', 'lager', 'pale', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/north-american-pilsner',
  }),
  craftMalt({
    id: 'ny-craft-genesee-valley-vienna',
    name: 'Genesee Valley Vienna',
    maker: 'New York Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.1,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A 2-row Vienna with a sweet, toasty flavor and light golden color, a shade sweeter than a straight base malt. Self-converting, so it can carry a festbier or balance the bitterness in a hoppier grist.',
    tags: ['kilned', 'vienna', 'toasty', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/genesee-valley-vienna',
  }),
  craftMalt({
    id: 'ny-craft-mt-marcy-munich',
    name: 'Mt. Marcy Munich',
    maker: 'New York Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Named for the highest peak in New York State and kilned for a deep toasted aroma. Enough enzyme to convert itself, which makes it the malt spine of an Oktoberfest as readily as a seasoning in a stout.',
    tags: ['kilned', 'munich', 'toasty', 'oktoberfest', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/mt-marcy-munich',
  }),
  craftMalt({
    id: 'ny-craft-sugar-pils',
    name: 'Sugar Pils (Dextrin Malt)',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.1,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      "The house take on a dextrin malt, kilned so gently that it keeps a base malt's enzyme. Improves body, foam and stability without adding color or flavor; five to fifteen percent is the usual dose.",
    tags: ['dextrin', 'body', 'head-retention', 'base', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/sugar-pils',
  }),
  craftMalt({
    id: 'ny-craft-seneca-soft-red-wheat',
    name: 'Seneca Soft Red Wheat',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 3.3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft red winter wheat from central New York, malted for a creamy sweetness with a mellow finish. Adds a smooth mouthfeel, a pleasant haze and better head retention than the white wheat beside it.',
    tags: ['base', 'wheat', 'red-wheat', 'haze', 'head-retention', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/seneca-soft-red-wheat',
  }),
  craftMalt({
    id: 'ny-craft-upstate-hard-red-wheat',
    name: 'Upstate Hard Red Wheat',
    maker: 'New York Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted from Warthog, an heirloom hard red wheat grown in central New York. Higher in protein than the soft wheats, so it builds more body and a denser head; good for part or all of a wheat beer base.',
    tags: ['base', 'wheat', 'heirloom', 'warthog', 'head-retention', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/upstate-hard-red-wheat',
  }),
  craftMalt({
    id: 'ny-craft-oatka-malted-oats',
    name: 'Oatka Malted Oats',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Oats taken through a full malting rather than flaked. Velvety smoothness and stronger head retention for hazy IPA and Belgian wit, with some enzyme of their own to help the mash along.',
    tags: ['adjunct', 'oats', 'hazy', 'mouthfeel', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/oatka-malted-oats',
  }),
  craftMalt({
    id: 'ny-craft-honeoye-honey-malt',
    name: 'Honeoye Honey Malt',
    maker: 'New York Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 20,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'A melanoidin malt kilned for an intense honey sweetness, bread crust and a golden-amber cast. It carries a hint of tartness but none of the bitterness of a roast; five to ten percent is loud enough.',
    tags: ['kilned', 'honey', 'melanoidin', 'aromatic', 'amber', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/oatka-malted-oats-c9cfa',
  }),
  craftMalt({
    id: 'ny-craft-keuka-coffee-malt',
    name: 'Keuka Coffee Malt',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5.5,
    colorLovibond: 175,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'A dark roast at the light end of the chocolate range: espresso, dark chocolate, nut and a caramel undertone. Sharpens the coffee note in a stout or porter, or adds complexity to a brown ale.',
    tags: ['roasted', 'coffee', 'chocolate', 'stout', 'porter', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/wicked-dark-jx227',
  }),
  craftMalt({
    id: 'ny-craft-batavia-biscuit',
    name: 'Batavia Biscuit',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Drum-roasted to a deep gold, tasting of freshly baked bread with a nutty, clean finish. Five to ten percent gives a pale ale or a lager a bakery depth no crystal malt can reach.',
    tags: ['roasted', 'biscuit', 'toasted', 'bready', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/batavia-biscuit',
  }),
  craftMalt({
    id: 'ny-craft-saranac-special-roast',
    name: 'Saranac Special Roast',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 3,
    colorLovibond: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'A drum roast with a full, rich malt taste and notes of bran flakes and sourdough. The toasty, faintly tangy edge suits English ales, nut brown ales and porters at five to ten percent.',
    tags: ['roasted', 'special-roast', 'sourdough', 'brown-ale', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/saranac-special-roast',
  }),
  craftMalt({
    id: 'ny-craft-chautauqua-chocolate',
    name: 'Chautauqua Chocolate',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 5.5,
    colorLovibond: 320,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Heavily toasted, with a strong roast flavor carrying chocolate and nut. A percent or two nudges the color of a brown ale; five or more darkens a porter or stout and takes the flavor with it.',
    tags: ['roasted', 'chocolate', 'brown-ale', 'porter', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/chautauqua-chocolate',
  }),
  craftMalt({
    id: 'ny-craft-buffalo-bitterless',
    name: 'Buffalo Bitterless',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4.5,
    colorLovibond: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Roasted from the hulless Buck Naked barley, which is why it gives deep color without the astringency a husked black malt brings. Made for schwarzbier and for oatmeal and sweet stouts that must stay smooth.',
    tags: ['roasted', 'black', 'debittered', 'schwarzbier', 'stout', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/buffalo-bitterless',
  }),
  craftMalt({
    id: 'ny-craft-wicked-dark',
    name: 'Wicked Dark',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 6,
    colorLovibond: 550,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'The deepest roast in the house, with coffee and a frank bitterness, and it darkens the foam as well as the beer. Offered on barley, rye or wheat; a percent or two is usually enough.',
    tags: ['roasted', 'black', 'coffee', 'stout', 'porter', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/wicked-dark',
  }),
  craftMalt({
    id: 'ny-craft-catskill-crystal-20',
    name: 'Catskill Crystal 20',
    maker: 'New York Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 6,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'The workhorse of the crystal range: sweet and candy-like with mild caramel and a deeper gold. Improves foam and mouthfeel as much as it adds flavor, which is why it turns up in almost every style.',
    tags: ['crystal', 'caramel', 'light', 'body', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/catskill-crystal-20',
  }),
  craftMalt({
    id: 'ny-craft-catskill-crystal-40',
    name: 'Catskill Crystal 40',
    maker: 'New York Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 5,
    colorLovibond: 40,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Sweet, mild caramel with a nutty edge and a fuller body behind it. The default crystal for pale ales and ambers, and light enough not to muddy a lager.',
    tags: ['crystal', 'caramel', 'amber', 'nutty', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/catskill-crystal-40',
  }),
  craftMalt({
    id: 'ny-craft-catskill-crystal-60',
    name: 'Catskill Crystal 60',
    maker: 'New York Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A pronounced caramel sweetness with the nuttiness the darker crystals bring. Fills out the body, holds the head and pushes the color towards deep gold.',
    tags: ['crystal', 'caramel', 'toffee', 'nutty', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/catskill-crystal-60',
  }),
  craftMalt({
    id: 'ny-craft-catskill-crystal-80',
    name: 'Catskill Crystal 80',
    maker: 'New York Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 4.5,
    colorLovibond: 80,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 12,
    needsMashing: false,
    description:
      'Burnt sugar and raisin over a full, nutty base, and a ruby-red cast to go with it. Built for robust porters and stouts, and it does the same work in a whiskey mash.',
    tags: ['crystal', 'caramel', 'dark', 'raisin', 'ruby', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/catskill-crystal-80',
  }),
  craftMalt({
    id: 'ny-craft-catskill-crystal-120',
    name: 'Catskill Crystal 120',
    maker: 'New York Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 3,
    colorLovibond: 125,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The dark end of the range: burnt sugar and prune over a deep red hue, complex and nutty rather than simply sweet. Small additions only, in the darkest and strongest beers.',
    tags: ['crystal', 'caramel', 'dark', 'prune', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/catskill-crystal-120',
  }),
  craftMalt({
    id: 'ny-craft-roasted-raw-oats-light',
    name: 'Roasted Raw Grains: Light',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Unmalted oats given a light roast: smooth and nutty with a light toffee finish. An unusual way to add complexity to a stout, a brown ale or anything experimental.',
    tags: ['roasted', 'oats', 'unmalted', 'raw-grain', 'toffee', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/roasted-raw-grains-light',
  }),
  craftMalt({
    id: 'ny-craft-roasted-raw-barley-black',
    name: 'Roasted Raw Grains: Black',
    maker: 'New York Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Unmalted barley taken to a deep roast with a light char. Dry, sharp and roasty, and the traditional route to the color and tan head of a stout.',
    tags: ['roasted', 'barley', 'unmalted', 'raw-grain', 'stout', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/roasted-raw-grains-black',
  }),
  craftMalt({
    id: 'ny-craft-brewers-barley-flakes',
    name: 'Brewers Barley Flakes',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 6,
    colorLovibond: 1.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Steam-conditioned barley flakes, pregelatinised so they need no milling. Add depth, a creamy smoothness and better head retention to porters, stouts and New England IPAs.',
    tags: ['adjunct', 'barley', 'flaked', 'body', 'head-retention', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/brewers-barley-flakes',
  }),
  craftMalt({
    id: 'ny-craft-brewers-wheat-flakes',
    name: 'Brewers Wheat Flakes',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 1.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Steam-conditioned unmalted wheat: protein for foam and a hazy cast, with no husk and no enzyme of its own. The traditional route to a witbier, and the quickest way to firm up a hazy IPA.',
    tags: ['adjunct', 'wheat', 'flaked', 'haze', 'head-retention', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/brewers-wheat-flakes',
  }),
  craftMalt({
    id: 'ny-craft-brewers-rye-flakes',
    name: 'Brewers Rye Flakes',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 6.5,
    colorLovibond: 1.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Mash-ready rye flakes that keep the grainy, spicy cracker essence of rye and dry the finish. Rice hulls are part of the plan once they are more than a seasoning.',
    tags: ['adjunct', 'rye', 'flaked', 'spicy', 'crisp', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/brewers-rye-flakes',
  }),
  craftMalt({
    id: 'ny-craft-brewers-oat-flakes',
    name: 'Brewers Oat Flakes',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 1.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Steam-conditioned oats for body, a velvety texture and the famous creamy head. What a stout, a porter or an East Coast IPA is thickened with.',
    tags: ['adjunct', 'oats', 'flaked', 'body', 'mouthfeel', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/brewers-oat-flakes',
  }),
  craftMalt({
    id: 'ny-craft-brewers-corn-flakes',
    name: 'Brewers Corn Flakes',
    maker: 'New York Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 82,
    moisturePercent: 6.5,
    colorLovibond: 0.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Pregelatinised maize, which lightens the body and dries the finish without a cereal cooker. The adjunct behind light American pilsner, cream ale and pre-Prohibition lager.',
    tags: ['adjunct', 'corn', 'flaked', 'pre-prohibition', 'cream-ale', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/brewers-corn-flakes',
  }),
  craftMalt({
    id: 'ny-craft-smoke-house-fruitwoods',
    name: 'Smoke House Series: Fruitwoods',
    maker: 'New York Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 6.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malt cold-smoked over cuttings from the farm orchard, which makes for a sweeter and fruitier smoke than beech. Subtle enough to season a wheat beer, and it will carry a smoked ale at higher rates.',
    tags: ['smoked', 'fruitwood', 'applewood', 'base', 'craft-malt', 'new-york'],
    url: 'https://www.newyorkcraftmalt.com/home-brew-shop-2bVbv-wGhcH/p/fruitwoods',
  }),

  // ---------------------------------------------------------------------------
  // Rabbit Hill Malthouse — Shiloh, New Jersey
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'rabbit-hill-pale',
    name: 'Matapeake 2-Row',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4.8,
    colorLovibond: 2.4,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Completely modified 2-row floor-malted on the family farm, lightly sweet and clean with a note of oats and raw dough. High enough in enzyme to carry a large adjunct charge, and mild enough to span any style.',
    tags: ['base', 'ale', 'two-row', 'craft-malt', 'new-jersey', 'floor-malted', 'estate'],
  }),
  craftMalt({
    id: 'rabbit-hill-pilsner',
    name: 'Farmhouse Pilsner',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 1.9,
    diastaticPowerLintner: 115,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Well-modified floor-malted pilsner from farm-grown 2-row: crisp and biscuit-forward with honeysuckle and sweet corn behind it. Took gold in the Pilsen category at the 2022 Malt Cup.',
    tags: ['base', 'pilsner', 'craft-malt', 'new-jersey', 'floor-malted', 'estate', 'award'],
  }),
  craftMalt({
    id: 'rabbit-hill-wheat',
    name: 'Wheat Malt',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Farm-grown wheat, floor-malted and modified far enough to be used at high inclusion. Lightly sweet with the chewy complexity of proved bread dough; plan on rice hulls at wheat-beer rates.',
    tags: ['base', 'wheat', 'craft-malt', 'new-jersey', 'floor-malted', 'estate', 'head-retention'],
  }),
  craftMalt({
    id: 'rabbit-hill-rye',
    name: 'Rye Malt',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.2,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Pale rye from farm-grown Danko, and deliberately not the peppery kind: bran flakes and sesame-crusted pumpernickel rather than raw spice. Adds malt complexity to ales and lagers, with the sticky mash that rye always brings.',
    tags: ['base', 'rye', 'danko', 'craft-malt', 'new-jersey', 'floor-malted', 'estate'],
  }),

  craftMalt({
    id: 'rabbit-hill-chillum-pale-ale',
    name: 'Chillum Pale Ale',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 3.2,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Farm 2-row cured at a higher kiln temperature for depth: toasted bread, over-baked crackers and clover honey, much like an English pale ale malt. Nutty and toasty enough to be the whole grist of a bitter.',
    tags: [
      'base',
      'pale-ale',
      'english',
      'toasty',
      'craft-malt',
      'new-jersey',
      'floor-malted',
      'estate',
    ],
  }),
  craftMalt({
    id: 'rabbit-hill-light-munich',
    name: 'Light Munich',
    maker: 'Rabbit Hill Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 9,
    diastaticPowerLintner: 84,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Stewed in the kiln for Maillard development but finished light enough to keep the enzyme for self-conversion. Sweet and bready, with a pale caramel note the maltster compares to wildflower honey.',
    tags: [
      'kilned',
      'munich',
      'bready',
      'self-converting',
      'craft-malt',
      'new-jersey',
      'floor-malted',
      'estate',
    ],
  }),
  craftMalt({
    id: 'rabbit-hill-oat',
    name: 'Oat Malt',
    maker: 'Rabbit Hill Malthouse',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 2.8,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted from hulled oats that keep their husk, so the mash filters far better than a naked oat malt allows. Lightly sweet and flour-forward, and the usual route to mouthfeel and haze.',
    tags: [
      'adjunct',
      'oats',
      'hazy',
      'mouthfeel',
      'craft-malt',
      'new-jersey',
      'floor-malted',
      'estate',
    ],
  }),
  craftMalt({
    id: 'rabbit-hill-spelt',
    name: 'Spelt Malt',
    maker: 'Rabbit Hill Malthouse',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Farm-grown spelt malted in the husk: warm, soft and rustic, tasting of white dough from a village bakery. More complex than wheat, and the husk gives a lauter wheat cannot match.',
    tags: [
      'base',
      'spelt',
      'ancient-grain',
      'rustic',
      'craft-malt',
      'new-jersey',
      'floor-malted',
      'estate',
    ],
  }),

  // ---------------------------------------------------------------------------
  // Valley Malt — Hadley, Massachusetts
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'valley-malt-pilsner',
    name: 'Pilsner Malt',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Northeast pioneer pilsner from New York and New England barley. Green-tea, honey and light cereal aromatics; the pale lager base of the regional craft-malt movement.',
    tags: ['base', 'pilsner', 'craft-malt', 'massachusetts', 'organic', 'local'],
    url: 'https://www.valleymalt.com/order-list/p/pilsner',
  }),
  craftMalt({
    id: 'valley-malt-pale',
    name: 'Pale Malt',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.4,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Everyday Northeast pale with deeper cereal flavor than the pilsner. Self-converting and grain-forward, the default ale base from Hadley’s regional growers.',
    tags: ['base', 'ale', 'craft-malt', 'massachusetts', 'organic', 'local'],
    url: 'https://www.valleymalt.com/order-list/p/pale',
  }),
  craftMalt({
    id: 'valley-malt-golden-valley-pale',
    name: 'Golden Valley Pale',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Highly modified English-style pale with light toast and honey graham-cracker flavor. Built for bitter, pale ale and IPA that want a warmer malt floor than straight 2-row.',
    tags: ['base', 'pale-ale', 'craft-malt', 'massachusetts', 'english-style'],
    url: 'https://www.valleymalt.com/order-list/p/golden-valley-pale',
  }),
  craftMalt({
    id: 'valley-malt-vienna',
    name: 'Vienna Malt',
    maker: 'Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Northeast Vienna with toasted-cracker sweetness and enough enzyme to convert a whole grist. The malt color in märzen, Vienna lager and amber ale.',
    tags: ['kilned', 'vienna', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/vienna-malt',
  }),
  craftMalt({
    id: 'valley-malt-warthog-wheat',
    name: 'Warthog Wheat',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Named for a Northeast-adapted wheat variety that doubles as a soil-building cover crop. Soft body and lasting foam for wheat beers and New England IPA.',
    tags: ['base', 'wheat', 'craft-malt', 'massachusetts', 'head-retention'],
    url: 'https://www.valleymalt.com/order-list/p/warthog-wheat',
  }),
  craftMalt({
    id: 'valley-malt-danko-rye',
    name: 'Danko Rye',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Danko winter rye, a Northeast workhorse variety malted in Hadley. Spicy cracker flavor for rye ale and rye whiskey; hulls keep the mash from sealing at high rates.',
    tags: ['base', 'rye', 'craft-malt', 'massachusetts', 'spicy'],
    url: 'https://www.valleymalt.com/order-list/p/danko-rye',
  }),
  craftMalt({
    id: 'valley-malt-crystal-twenty',
    name: 'Crystal Twenty',
    maker: 'Valley Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Light crystal from regional barley: honey and light caramel without much color. Five to ten percent rounds a pale ale or adds a gold tint to a lager.',
    tags: ['crystal', 'caramel', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/crystal-twenty',
  }),
  craftMalt({
    id: 'valley-malt-chocolate',
    name: 'Chocolate Malt',
    maker: 'Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Northeast barley roasted to chocolate color. Cocoa and coffee without the burnt edge of black malt; two to eight percent in porter, stout and brown ale.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/chocolate-malt',
  }),
  craftMalt({
    id: 'valley-malt-roc-pilsner',
    name: 'Regenerative Organic Pilsner',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The Regenerative Organic Certified pilsner: clean and crisp with honey sweetness and light biscuit. Same malt as the conventional pilsner, grown to a standard that certifies the soil as well as the crop.',
    tags: ['base', 'pilsner', 'lager', 'organic', 'regenerative', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/pilsner-roc',
  }),
  craftMalt({
    id: 'valley-malt-roc-pale',
    name: 'Regenerative Organic Pale',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Regenerative Organic Certified pale malt: light biscuit over cereal aromatics, for traditional ales and IPAs.',
    tags: ['base', 'pale', 'ale', 'organic', 'regenerative', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/pale-roc',
  }),
  craftMalt({
    id: 'valley-malt-light-munich',
    name: 'Light Munich',
    maker: 'Valley Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 7.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Sweet, nutty and brown-sugared with a ruby cast and the Maillard complexity that comes with a longer kiln. Enough enzyme to make up half a grist.',
    tags: ['kilned', 'munich', 'malty', 'amber', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/light-munich',
  }),
  craftMalt({
    id: 'valley-malt-va-va-voam',
    name: 'Va Va Voam',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'An under-modified dextrine malt, and an unusual one: honeysuckle, oatmeal, cucumber, toast and oat straw rather than the flavorless body a conventional dextrine malt gives. Built for foam and mouthfeel.',
    tags: ['dextrin', 'body', 'head-retention', 'under-modified', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/va-va-voam',
  }),
  craftMalt({
    id: 'valley-malt-harris-wheat',
    name: 'Harris Wheat',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'A soft white heritage wheat with a nutty sugar-cookie character and unusually high extraction for a wheat malt.',
    tags: ['wheat', 'heritage', 'harris', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/harris-wheat',
  }),
  craftMalt({
    id: 'valley-malt-malted-maize',
    name: 'Organic Malted Maize',
    maker: 'Valley Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 10,
    colorLovibond: 10,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Organic corn, malted rather than flaked, and it tastes roasted rather than neutral. For light and Mexican lagers where the corn is meant to be noticed.',
    tags: [
      'corn',
      'maize',
      'adjunct',
      'organic',
      'lager',
      'gluten-free',
      'craft-malt',
      'massachusetts',
    ],
    url: 'https://www.valleymalt.com/order-list/p/organic-malted-maize',
  }),
  craftMalt({
    id: 'valley-malt-biscuit',
    name: 'Biscuit Malt',
    maker: 'Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4,
    colorLovibond: 10,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A light roast: pizza crust, biscuit and Triscuit, with very little color for the flavor it brings. Five to ten percent adds bakery depth to a pale beer.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/biscuit-malt',
  }),
  craftMalt({
    id: 'valley-malt-coffee',
    name: 'Coffee Malt',
    maker: 'Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Their brown malt, roasted to a strong and clean coffee character. The malt to reach for when a porter should taste of coffee without any being added.',
    tags: ['roasted', 'coffee', 'brown', 'porter', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/coffee-malt',
  }),
  craftMalt({
    id: 'valley-malt-chocolate-rye',
    name: 'Chocolate Rye',
    maker: 'Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Rye roasted to chocolate depth, and noticeably less bitter than chocolate barley malt: black pepper, chocolate and shortbread. A spicy roast for a roggenbier or a stout.',
    tags: ['roasted', 'chocolate', 'rye', 'spice', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/chocolate-rye',
  }),
  craftMalt({
    id: 'valley-malt-chocolate-wheat',
    name: 'Chocolate Wheat',
    maker: 'Valley Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 280,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Wheat roasted dark, which is smoother than roasting barley because there is no husk to contribute astringency. Dark toast and chocolate bread without the bitterness.',
    tags: ['roasted', 'chocolate', 'wheat', 'smooth', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/chocolate-wheat',
  }),
  craftMalt({
    id: 'valley-malt-roc-warthog-wheat',
    name: 'Regenerative Organic Warthog Wheat',
    maker: 'Valley Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 5.5,
    colorLovibond: 2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'The Regenerative Organic Certified Warthog: honey, sweetgrass and shredded wheat, with the head retention a hazy IPA wants.',
    tags: ['wheat', 'organic', 'regenerative', 'warthog', 'hazy', 'craft-malt', 'massachusetts'],
    url: 'https://www.valleymalt.com/order-list/p/warthog-wheat-roc',
  }),
  // ---------------------------------------------------------------------------
  // Murmuration Malts — Bloomfield, New York
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'murmuration-2-row-violetta',
    name: '2 Row (Violetta)',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.1,
    moisturePercent: 3.7,
    colorLovibond: 2.3,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Finger Lakes two-row spring barley of the Violetta variety, malted in a converted Bloomfield service station. The published lab sheet shows unusually high enzyme reserve for a pale base malt, so it carries a large adjunct or specialty load comfortably while keeping a clean, lightly grainy backbone.',
    tags: ['base', 'pale', 'two-row', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-pilsner-li-scala',
    name: 'Pilsner (LI Scala)',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82.2,
    moisturePercent: 4.7,
    colorLovibond: 1.98,
    diastaticPowerLintner: 127,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A pale lager malt from the Scala barley variety, kilned gently enough to keep the color under two degrees. Extract is the highest of the maker’s barley malts and the wort runs slightly hazy per their own analysis. Suits lagers and delicate ales where malt character should stay in the background.',
    tags: ['base', 'pilsner', 'lager', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-blond-munich',
    name: 'Blond Munich',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81.4,
    moisturePercent: 2.7,
    colorLovibond: 4.23,
    diastaticPowerLintner: 131,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A pale Munich sitting just above four degrees Lovibond, kilned warm enough for bread-crust depth yet still holding enough diastatic power to convert itself and more. Low alpha amylase relative to total power suggests a slower, beta-driven conversion; give it a patient rest.',
    tags: ['munich', 'kilned', 'malty', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-dark-honey',
    name: 'Dark Honey',
    maker: 'Murmuration Malts',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.7,
    moisturePercent: 4.3,
    colorLovibond: 37,
    diastaticPowerLintner: 89,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'A deeply kilned amber malt that keeps a surprising 89 °Lintner despite its color, with high soluble protein and free amino nitrogen and a slow published filtration time. Use it for toasty sweetness in browns and bocks. The 20 percent ceiling is a style-typical suggestion, not a figure the maltster publishes.',
    tags: ['kilned', 'toasted', 'amber', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-rye',
    name: 'Rye Malt',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 88.7,
    moisturePercent: 4.4,
    colorLovibond: 3.67,
    diastaticPowerLintner: 99,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Malted New York rye with a very high published extract and a slow, hazy runoff, as huskless grain tends to give. Brings the dry, peppery bite rye is prized for. The 30 percent ceiling reflects normal practice for lauter performance rather than a limit the maltster states.',
    tags: ['rye', 'specialty-grain', 'spicy', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-soft-red-wheat',
    name: 'Soft Red Wheat Malt',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85.3,
    moisturePercent: 4.7,
    colorLovibond: 2.04,
    diastaticPowerLintner: 131,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft red winter wheat malted locally, pale in color with strong enzyme carry and generous extract. Lends protein for head retention and a soft, doughy fullness. The 60 percent suggestion is conventional for wheat beers, not a maker-published maximum.',
    tags: ['wheat', 'base', 'head-retention', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-organic-soft-white-wheat',
    name: 'Organic Soft White Wheat Malt',
    maker: 'Murmuration Malts',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.1,
    moisturePercent: 5.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 175,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Organic soft white wheat, the palest malt in the range and the most enzymatic at 175 °Lintner. Higher total protein than the barley malts, which is what builds a lasting head. The 60 percent ceiling is a style-typical suggestion rather than a published limit.',
    tags: ['wheat', 'organic', 'base', 'new-york', 'local'],
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'murmuration-caramel-60',
    name: 'Caramel 60',
    maker: 'Murmuration Malts',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78.5,
    moisturePercent: 2.2,
    colorLovibond: 66,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'A mid-range caramel malt stewed and kilned in-house; the maker publishes moisture, extract and a measured 66 °SRM color but no enzyme figure, so the zero diastatic power and the 15 percent ceiling here are class-typical values rather than the maltster’s own. Adds toffee sweetness and body.',
    tags: ['crystal', 'caramel', 'sweetness', 'new-york', 'local'],
  }),
  craftMalt({
    id: 'murmuration-caramel-100',
    name: 'Caramel 100',
    maker: 'Murmuration Malts',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 2.3,
    colorLovibond: 100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'The darker of the two house caramel malts, pushing into raisin and burnt-sugar territory. Moisture, extract and color come from the maker’s published analysis; the zero diastatic power and the 10 percent ceiling are class-typical figures, as no enzyme or grist data is published.',
    tags: ['crystal', 'caramel', 'dark', 'new-york', 'local'],
  }),
  // ---------------------------------------------------------------------------
  // Stone Path Malt — Wareham, Massachusetts
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'stone-path-noreast-gold-2-row',
    name: 'Nor’East Gold 2-Row',
    maker: 'Stone Path Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Drum-kilned below 90 °C from New England two-row spring barley, and the most enzymatic of the Nor’East base malts. The maker states it will carry a full grain bill on its own. Color is the midpoint of a published 1.5–2.5 °L range; the moisture row on their spec table is blank, so 4.5 percent is a class-typical stand-in.',
    tags: ['base', 'pale', 'two-row', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-gold/',
  }),
  craftMalt({
    id: 'stone-path-noreast-pilsner',
    name: 'Nor’East Pilsner',
    maker: 'Stone Path Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The palest malt in the Wareham house range, drum-kilned lightly for a clean, fresh grain flavor. Aimed at lagers, light session ales and hazy IPAs. Color is the midpoint of the published 1.4–2.2 °L specification.',
    tags: ['base', 'pilsner', 'lager', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-pilsner/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'stone-path-noreast-pale',
    name: 'Nor’East Pale',
    maker: 'Stone Path Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.9,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'An English-style pale malt drum-kilned above 95 °C, giving a nutty, bread-crust sweetness while retaining enough enzyme to make up the whole grist. Color is the midpoint of the published 2.3–3.5 °L range.',
    tags: ['base', 'pale', 'english-style', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-pale/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'stone-path-noreast-vienna',
    name: 'Nor’East Vienna',
    maker: 'Stone Path Malt',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A German-style Vienna from local two-row, kilned warm for golden color and a toasted bread character but still self-converting at a full-grist rate. Color is the midpoint of the published 2.8–4.2 °L range. Built for Vienna lagers and Märzen.',
    tags: ['vienna', 'kilned', 'lager', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-vienna/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'stone-path-noreast-munich',
    name: 'Nor’East Munich',
    maker: 'Stone Path Malt',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 6,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A drum-kilned amber specialty the maker says can serve as the base for bocks and dark lagers, bringing caramel and bread notes with it. Color is the midpoint of the published 5.0–7.0 °L range, and the 80 °Lintner minimum is enough to convert the mash unaided.',
    tags: ['munich', 'kilned', 'malty', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-munich/',
    provenance: 'spec-sheet',
  }),
  craftMalt({
    id: 'stone-path-noreast-kara-10l',
    name: 'Nor’East Kara 10L',
    maker: 'Stone Path Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 80,
    moisturePercent: 5.5,
    colorLovibond: 12.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'A light caramel malt for dark gold to amber tone and gentle toffee notes. Moisture, extract and a 10–15 °L color range are published; color here is that midpoint. The maker lists no enzyme figure or grist limit, so the zero diastatic power and 20 percent ceiling are class-typical values.',
    tags: ['crystal', 'caramel', 'light', 'massachusetts', 'local'],
    url: 'https://stonepathmalt.com/product/noreast-kara-10l/',
  }),
  // ---------------------------------------------------------------------------
  // Niagara Malt — Western New York (the Guild says Clarence Center; the maltster’s own material says Cambria)
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'niagara-organic-pilsner',
    name: 'Organic Pilsner Malt',
    maker: 'Niagara Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A NOFA-NY certified organic pilsner malt from a small western New York farm that malts its own barley using renewable power. Every figure listed here is class-typical for an organic pilsner malt; the maltster names the product but publishes no analysis of any kind.',
    tags: ['base', 'pilsner', 'organic', 'new-york', 'local'],
  }),
  craftMalt({
    id: 'niagara-organic-pale',
    name: 'Organic Pale Malt',
    maker: 'Niagara Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 3,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Certified organic pale malt from farm-grown western New York barley, malted on site. All values shown are class-typical for a pale ale malt rather than measured figures, since the maker lists the malt by name only and provides no specification sheet.',
    tags: ['base', 'pale', 'organic', 'new-york', 'local'],
  }),
  craftMalt({
    id: 'niagara-organic-vienna',
    name: 'Organic Vienna Malt',
    maker: 'Niagara Malt',
    type: 'base',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Certified organic Vienna malt for golden color and light toast. The maltster names it among its current offerings but publishes no analysis, so extract, moisture, color, enzyme power and the grist ceiling here are all class-typical values rather than measured ones.',
    tags: ['vienna', 'kilned', 'organic', 'new-york', 'local'],
  }),
  craftMalt({
    id: 'niagara-organic-cara-pils',
    name: 'Organic Cara-Pils Malt',
    maker: 'Niagara Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'A very pale dextrine malt used for body and foam without added color or sweetness. Certified organic and named on the maker’s malt page, but no specifications are published at all, so every number given here is a class-typical value for cara-pils rather than the maltster’s own measurement.',
    tags: ['crystal', 'dextrine', 'organic', 'body', 'new-york'],
  }),

  // ---------------------------------------------------------------------------
  // Bear Branch Malt — Westminster, Maryland
  //
  // A malthouse on a fourth-generation Carroll County farm, running a 4-tonne
  // Saladin box. Grain comes from partner growers nearby. Names, flavor notes
  // and colour come off their single products page (bearbranchmalt.com/products),
  // which prints an SRM range for every malt. Each row takes the midpoint of
  // that range, stored as °L. They publish no extract, moisture or DP, so those
  // are class-typical, and the provenance stays `representative`. All fourteen
  // products share that one page and none has a page of its own, so no row
  // carries a `url` (a link may not be shared).
  //
  // "Munich Dark" is described on their page as "our Caramunich style malt",
  // so it is typed as a crystal with no enzyme, not as a kilned Munich. The
  // name is theirs; the type follows their description. Golden Honey and
  // Double Honey are described as melanoidin malts germinated for honey
  // character, so they follow the Gambrinus Honey row: kilned-toasted, no
  // enzyme.
  //
  // The "Distillers" malt is sold as high-enzyme, but they print no DP, so the
  // figure below is the class value for a distilling malt and not theirs.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'bear-branch-pilsner',
    name: 'Pilsner',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Two-row germinated cool and slow and lightly kilned to stay pale, with a bright malty sweetness, a hint of honey and light pastry crust. The maker publishes 1.4–1.8 SRM; extract and enzyme are class-typical.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'maryland', 'local'],
  }),
  craftMalt({
    id: 'bear-branch-rustic-pils',
    name: 'Rustic Pils',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 1.4,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Deliberately a little under-modified and very pale (1.2–1.6 SRM), with a sharp malty sweetness. The maker recommends a decoction mash and old-world lager styles. Extract and enzyme are class-typical.',
    tags: ['base', 'pilsner', 'under-modified', 'decoction', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-pale',
    name: 'Pale',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Their everyday two-row, malty-sweet with lightly toasted cereal, pitched as a base for most styles. Color is the maker’s 2.1–2.4 SRM; extract and enzyme are class-typical.',
    tags: ['base', 'two-row', 'craft-malt', 'maryland', 'local'],
  }),
  craftMalt({
    id: 'bear-branch-pale-ale',
    name: 'Pale Ale',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 3,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Kilned a shade darker than their Pale, at 2.7–3.2 SRM, for a richer malt sweetness and a biscuit note. It suits English-leaning and malt-forward ales as the whole base. Extract and enzyme are class-typical.',
    tags: ['base', 'ale', 'biscuit', 'craft-malt', 'maryland', 'local'],
  }),
  craftMalt({
    id: 'bear-branch-distillers',
    name: 'Distillers',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 1.7,
    diastaticPowerLintner: 150,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Two-row kilned gently to keep its enzymes (1.5–1.9 SRM), meant for spirit mash bills with raw grain in them. The maker publishes no diastatic power, so the figure shown is class-typical for a distilling malt.',
    tags: ['base', 'distilling', 'high-enzyme', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-pale-wheat',
    name: 'Pale Wheat',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted Maryland soft red winter wheat, malty-sweet and softly bready, for body and head retention. Color is the maker’s 1.5–2.5 SRM; the rest is class-typical. Use rice hulls at high rates.',
    tags: ['base', 'wheat', 'craft-malt', 'maryland', 'head-retention'],
  }),
  craftMalt({
    id: 'bear-branch-pale-rye',
    name: 'Pale Rye',
    maker: 'Bear Branch Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted Maryland rye with a light malt sweetness, a hint of honey and baking spice, at 2–3 SRM. For rye whiskey and for rye ales at 10–30 %; expect a sticky mash. Extract and enzyme are class-typical.',
    tags: ['base', 'rye', 'spicy', 'craft-malt', 'maryland', 'local'],
  }),
  craftMalt({
    id: 'bear-branch-vienna',
    name: 'Vienna',
    maker: 'Bear Branch Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    colorLovibond: 4.5,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Light toast and biscuit over malty sweetness at 4–5 SRM. The maker offers it as a base malt or as a specialty for color and complexity. Extract and enzyme are class-typical.',
    tags: ['kilned', 'vienna', 'lager', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-munich-light',
    name: 'Munich Light',
    maker: 'Bear Branch Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4,
    colorLovibond: 13,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Pronounced malt character with light caramel and pastry crust. At 11–15 SRM it is darker than most light Munichs, so 10–30 % goes a long way in a bock or amber. Extract and enzyme are class-typical.',
    tags: ['kilned', 'munich', 'malty', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-munich-dark',
    name: 'Munich Dark',
    maker: 'Bear Branch Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 31.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Despite the name, the maker calls this a Caramunich-style malt: plenty of caramel and toasted bread on a sweet malt base, at 28–35 SRM. Use 5–15 % in dunkel, bock and amber ales. Extract is class-typical.',
    tags: ['crystal', 'caramunich', 'caramel', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-munich-rye',
    name: 'Munich Rye',
    maker: 'Bear Branch Malt',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 12.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Rye kilned Munich-style (10–15 SRM) for a pronounced malt sweetness with light bread crust. Good in a roggenbier or a rye amber at 10–30 %. Extract and enzyme are class-typical.',
    tags: ['kilned', 'rye', 'munich', 'malty', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-caramel-rye',
    name: 'Caramel Rye',
    maker: 'Bear Branch Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5,
    colorLovibond: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Rye taken to caramel at 40–50 SRM: sweet caramel, dried fruit and toasted pie crust with the grain’s spice underneath. Five to ten percent in rye porters, ambers and brown ales. Extract is class-typical.',
    tags: ['crystal', 'rye', 'caramel', 'dried-fruit', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-golden-honey',
    name: 'Golden Honey',
    maker: 'Bear Branch Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 15,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'A melanoidin malt germinated to develop honey character and kilned lightly (13–17 SRM), bright with honey and biscuit. Five to fifteen percent lifts a blonde, a pale ale or a braggot. Extract is class-typical.',
    tags: ['kilned', 'honey', 'melanoidin', 'craft-malt', 'maryland'],
  }),
  craftMalt({
    id: 'bear-branch-double-honey',
    name: 'Double Honey',
    maker: 'Bear Branch Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 52.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'The same honey-malt germination kilned much further, to 45–60 SRM: a deep honey character with dark bread crust. Use under ten percent in brown ales, bocks and dark braggots. Extract is class-typical.',
    tags: ['kilned', 'honey', 'melanoidin', 'bread-crust', 'craft-malt', 'maryland'],
  }),

  // ---------------------------------------------------------------------------
  // Thrall Family Malt — Windsor, Connecticut
  //
  // **Names only; every number is class-typical.** Thrall publish no product
  // list: their "Our Malt" page describes the process and asks brewers to
  // phone for samples. Only two names can be sourced. "2-row Pale" is on
  // their own site (the last post of their 2017 build-out log, "Bagging our
  // 2-row Pale!"), and East Hartford's Brew & Wine Hobby sells it as "Thrall
  // Family Malt 2-row". "Pils Malt" comes from the Underground Brewers of
  // Connecticut's June 2024 tour write-up, which also gives the kilning times
  // (about 20 h for the Pils Malt, 12–13 h for the 2-row).
  //
  // The same write-up mentions caramel malts, a smoked malt, wheat, rye, oats,
  // emmer, triticale and flaked grains, but names none of them. They are left
  // out rather than given invented names. If Thrall ever publish a list, those
  // are the next rows.
  //
  // The grain is their own: 600 acres in Windsor (Valley Malt's grower profile),
  // plus a New Hampshire farm bought in 2023. "Estate" is fair for the barley
  // they grow themselves, but they also buy in, so the rows below do not
  // claim it.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'thrall-pils-malt',
    name: 'Pils Malt',
    maker: 'Thrall Family Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Connecticut barley kilned slowly at low temperature (about 20 hours) to stay pale, for lagers, pilsners and farmhouse ales as the whole grist. All values shown are class-typical because the maker lists the malt by name only.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'connecticut', 'local'],
  }),
  craftMalt({
    id: 'thrall-2-row-pale',
    name: '2-Row Pale',
    maker: 'Thrall Family Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house two-row base from Connecticut-grown barley, kilned a little warmer and shorter than the Pils Malt. All values shown are class-typical because the maker lists the malt by name only.',
    tags: ['base', 'two-row', 'craft-malt', 'connecticut', 'local'],
  }),
];
